import React, { useEffect, useState, useMemo, useCallback, Suspense } from 'react';
import { motion } from 'framer-motion';
import Layout from '../../elements/Layout';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchCustomerById, fetchCustomerContracts, getCustomerPayments, getCustomerProperties, sendPasswordSetEmail } from '../../utils/api/api';
import moment from 'moment';
import toast from 'react-hot-toast';
import Modal from '../../elements/Modal';

// Lazy loaded components with preloading functions
const CustomerStatement = React.lazy(() => import('./CustomerStatement'));
const AddCustomerPayment = React.lazy(() => import('./AddCustomerPayment'));
const CustomerOrders = React.lazy(() => import('./CustomerOrders'));

// Preload components - call these when hovering over related tabs
const preloadStatement = () => import('./CustomerStatement');
const preloadPayment = () => import('./AddCustomerPayment');
const preloadOrders = () => import('./CustomerOrders');

// Lucide Icons
import {
    User,
    Home,
    DollarSign,
    Phone,
    Mail,
    Calendar,
    MapPin,
    Users,
    Bell,
    Eye,
    Edit,
    Plus,
    ExternalLink,
    CreditCard,
    BarChart3,
    FileText,
    CheckCircle,
    Clock,
    ShoppingBag,
    Receipt,
    Activity,
    AlertCircle,
    Loader2
} from 'lucide-react';

// Animation variants - simplified for better performance
const fadeIn = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.2 }
};

// Loading fallback component for Suspense
const TabLoadingFallback = () => (
    <div className="flex justify-center items-center p-8">
        <div className="flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-gray-600">Loading content...</p>
        </div>
    </div>
);

// Optimized reusable components
const CardWithIcon = React.memo(({ icon, title, gradient, children }) => {
    return (
        <motion.div
            className={`bg-gradient-to-br ${gradient} p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300`}
            whileHover={{ y: -5 }}
        >
            <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
                {icon}
                {title}
            </h3>
            <div className="space-y-3 text-sm">
                {children}
            </div>
        </motion.div>
    );
});

const DataRow = React.memo(({ label, value }) => (
    <div className="flex justify-between items-center">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-800">{value || 'N/A'}</span>
    </div>
));

const Badge = React.memo(({ type, text }) => {
    const styles = {
        active: "bg-green-100 text-green-700 border border-green-200",
        pending: "bg-yellow-100 text-yellow-700 border border-yellow-200",
        inactive: "bg-gray-100 text-gray-700 border border-gray-200",
        success: "bg-blue-100 text-blue-700 border border-blue-200",
        primary: "bg-purple-100 text-purple-700 border border-purple-200",
        warning: "bg-orange-100 text-orange-700 border border-orange-200",
        danger: "bg-red-100 text-red-700 border border-red-200",
        secondary: "bg-yellow-100 text-yellow-700 border border-yellow-200"
    };

    return (
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[type] || styles.secondary}`}>
            {text}
        </span>
    );
});

const TabButton = React.memo(({ label, icon, isActive, onClick, onMouseEnter }) => (
    <motion.button
        whileHover={{ y: -2 }}
        whileTap={{ y: 0 }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-200 ${isActive
                ? 'text-yellow-600 bg-yellow-50 border border-yellow-200 shadow-sm font-medium'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
    >
        {icon}
        <span>{label}</span>
    </motion.button>
));

const OverviewTab = React.memo(({ info }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <CardWithIcon
            icon={<Home size={18} className="text-blue-500" />}
            title="Property Summary"
            gradient="from-white to-blue-50"
        >
            <DataRow label="Total Plots" value={info?.stats?.orders || 0} />
            <DataRow label="Active Contracts" value={info?.stats?.contracts || 0} />
            <DataRow label="Total Orders" value={info?.stats?.orders || 0} />
            <DataRow label="Total Documents" value={info?.stats?.documents || 0} />
        </CardWithIcon>

        <CardWithIcon
            icon={<DollarSign size={18} className="text-green-500" />}
            title="Financial Summary"
            gradient="from-white to-green-50"
        >
            <DataRow label="Total Payments" value={info?.stats?.payments || 0} />
            {/* You can add more financial data here */}
            <div className="mt-4 flex justify-center">
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-white border border-green-200 text-green-600 rounded-xl text-xs font-medium shadow-sm flex items-center gap-2"
                    onClick={() => document.getElementById('payments-tab-button')?.click()}
                >
                    <Eye size={14} />
                    <span>View Payments</span>
                </motion.button>
            </div>
        </CardWithIcon>

        <CardWithIcon
            icon={<Users size={18} className="text-purple-500" />}
            title="1st Next of Kin"
            gradient="from-white to-purple-50"
        >
            <DataRow label="Name" value={info?.next_of_kin1_name} />
            <DataRow label="Phone" value={info?.next_of_kin1_phone} />
            <DataRow label="Id. No." value={info?.next_of_kin1_idNo} />
            <DataRow label="Relationship" value={info?.next_of_kin1_relationship} />
        </CardWithIcon>

        <CardWithIcon
            icon={<Users size={18} className="text-purple-500" />}
            title="2nd Next of Kin"
            gradient="from-white to-purple-50"
        >
            <DataRow label="Name" value={info?.next_of_kin2_name} />
            <DataRow label="Phone" value={info?.next_of_kin2_phone} />
            <DataRow label="Id. No." value={info?.next_of_kin2_idNo} />
            <DataRow label="Relationship" value={info?.next_of_kin2_relationship} />
        </CardWithIcon>
    </div>
));

const PlotsTab = React.memo(({ plots, navigate }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plots.length > 0 ? (
            plots.map(property => (
                <motion.div
                    key={property.id}
                    whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                    className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden cursor-pointer transition-all duration-300"
                    onClick={() => navigate(`/dashboard/properties/${property.id}`)}
                >
                    <div className="h-3 bg-yellow-500"></div>
                    <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="font-bold text-gray-800">{property.property_number}</h3>
                            <Badge
                                type={property.status === 'under_contract' ? 'warning' : 'success'}
                                text={property.status.replace('_', ' ')}
                            />
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm">
                                <FileText size={16} className="text-gray-500" />
                                <span className="text-gray-700 font-medium">{property.title_number || 'No title number'}</span>
                            </div>

                            <div className="flex items-center gap-2 text-sm">
                                <Home size={16} className="text-gray-500" />
                                <span className="text-gray-600">Size:</span>
                                <span className="text-gray-800 font-medium">{property.size || 'N/A'}</span>
                            </div>
                        </div>

                        <div className="mt-4 flex justify-center">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="w-full px-4 py-2 bg-yellow-50 text-yellow-600 border border-yellow-200 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                            >
                                <ExternalLink size={16} />
                                <span>View Details</span>
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            ))
        ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
                <Home size={48} className="text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Plots Found</h3>
                <p className="text-gray-500 text-center max-w-md mb-6">
                    This customer doesn't have any property plots associated with their account.
                </p>
            </div>
        )}
    </div>
));

const ContractsTab = React.memo(({ contracts, navigate }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {contracts.length > 0 ? (
            contracts.map((contract) => (
                <motion.div
                    key={contract.id}
                    whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                    className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden"
                >
                    <div className="h-2 bg-gradient-to-r from-yellow-400 to-yellow-500"></div>
                    <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="font-bold text-gray-800 leading-tight">{contract.title}</h3>
                            <Badge
                                type={contract.status === 'active' ? 'active' : 'pending'}
                                text={contract.status}
                            />
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-sm">
                                <FileText size={16} className="text-gray-500" />
                                <span className="text-gray-600">Type:</span>
                                <span className="text-gray-800 font-medium capitalize">
                                    {contract?.type?.replace('_', ' ') || 'N/A'}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 text-sm">
                                <Calendar size={16} className="text-gray-500" />
                                <span className="text-gray-600">Start Date:</span>
                                <span className="text-gray-800 font-medium">
                                    {contract?.start_date ? moment(contract.start_date).format('MMM Do YYYY') : 'N/A'}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 text-sm">
                                <CheckCircle size={16} className="text-gray-500" />
                                <span className="text-gray-600">Status:</span>
                                <span className="text-gray-800 font-medium capitalize">{contract.status}</span>
                            </div>
                        </div>

                        <div className="mt-6">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate(`/dashboard/contracts/${contract.id}/view`)}
                                className="w-full px-4 py-2.5 bg-yellow-500 text-white rounded-xl text-sm font-medium shadow-sm flex items-center justify-center gap-2"
                            >
                                <Eye size={16} />
                                <span>View Contract</span>
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            ))
        ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
                <FileText size={48} className="text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Contracts Found</h3>
                <p className="text-gray-500 text-center max-w-md mb-6">
                    This customer doesn't have any contracts associated with their account.
                </p>
            </div>
        )}
    </div>
));

const PaymentsTab = React.memo(({ payments, loading, handleAddPayment }) => (
    <div>
        <div className="flex justify-end mb-6">
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddPayment}
                className="px-4 py-2.5 bg-yellow-500 text-white rounded-xl text-sm font-medium shadow-sm flex items-center gap-2"
            >
                <Plus size={16} />
                <span>Add Payment</span>
            </motion.button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
                <div className="col-span-full flex justify-center py-12">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                    >
                        <Loader2 size={32} className="text-gray-400" />
                    </motion.div>
                </div>
            ) : payments.length > 0 ? (
                payments.map((payment) => (
                    <motion.div
                        key={payment.id}
                        whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                        className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden"
                    >
                        <div className="h-2 bg-gradient-to-r from-green-400 to-green-500"></div>
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <Badge
                                        type="success"
                                        text={payment.method || 'Payment'}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {moment(payment?.createdAt).format('MMM Do, YYYY')}
                                    </p>
                                </div>
                                <h3 className="font-bold text-xl text-gray-800">
                                    KES {Number(payment?.amount).toLocaleString()}
                                </h3>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm">
                                    <CreditCard size={16} className="text-gray-500" />
                                    <span className="text-gray-600">Transaction:</span>
                                    <span className="text-gray-800 font-medium">
                                        {payment?.transaction_number || payment?.transaction_id || 'N/A'}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2 text-sm">
                                    <Clock size={16} className="text-gray-500" />
                                    <span className="text-gray-600">Time:</span>
                                    <span className="text-gray-800 font-medium">
                                        {moment(payment?.createdAt).format('h:mm A')}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-6">
                                <Link
                                    to={`/dashboard/payments/${payment?.id}/receipt`}
                                    className="block w-full"
                                >
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="w-full px-4 py-2.5 bg-yellow-500 text-white rounded-xl text-sm font-medium shadow-sm flex items-center justify-center gap-2"
                                    >
                                        <Receipt size={16} />
                                        <span>View Receipt</span>
                                    </motion.button>
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                ))
            ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
                    <DollarSign size={48} className="text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-800 mb-2">No Payments Found</h3>
                    <p className="text-gray-500 text-center max-w-md mb-6">
                        This customer hasn't made any payments yet.
                    </p>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleAddPayment}
                        className="px-4 py-2.5 bg-yellow-500 text-white rounded-xl text-sm font-medium shadow-sm flex items-center gap-2"
                    >
                        <Plus size={16} />
                        <span>Add First Payment</span>
                    </motion.button>
                </div>
            )}
        </div>
    </div>
));

const ActivityTab = React.memo(({ audits }) => (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
        {audits?.length > 0 ? (
            <ul className="space-y-4">
                {audits.map((audit) => (
                    <motion.li
                        key={audit.id}
                        whileHover={{ y: -2 }}
                        className="flex items-start gap-4 p-4 border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow bg-white"
                    >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                            <Bell size={18} />
                        </div>
                        <div className="flex-1">
                            <p className="text-gray-800 font-medium mb-1">{audit.description}</p>
                            <span className="text-gray-500 text-sm">
                                {moment(audit.createdAt).format("MMM Do YYYY, h:mm A")}
                            </span>
                        </div>
                    </motion.li>
                ))}
            </ul>
        ) : (
            <div className="flex flex-col items-center justify-center py-16">
                <Activity size={48} className="text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Activity Found</h3>
                <p className="text-gray-500 text-center max-w-md">
                    There's no recent activity for this customer.
                </p>
            </div>
        )}
    </div>
));

const CustomerProfile = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [tabsLoaded, setTabsLoaded] = useState({ overview: true });
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [contracts, setContracts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [add, setAdd] = useState(false);
    const [info, setInfo] = useState({});
    const [plots, setPlots] = useState([]);
    const { id } = useParams();
    const navigate = useNavigate();
    const customer_id = id;

    const tabs = useMemo(() => [
        {
            id: 'overview',
            label: 'Overview',
            icon: <BarChart3 size={18} />,
            preload: () => setTabsLoaded(prev => ({ ...prev, overview: true }))
        },
        {
            id: 'plots',
            label: 'Plots',
            icon: <Home size={18} />,
            preload: () => setTabsLoaded(prev => ({ ...prev, plots: true }))
        },
        {
            id: 'contracts',
            label: 'Contracts',
            icon: <FileText size={18} />,
            preload: () => setTabsLoaded(prev => ({ ...prev, contracts: true }))
        },
        {
            id: 'payments',
            label: 'Payments',
            icon: <DollarSign size={18} />,
            preload: () => {
                preloadPayment();
                setTabsLoaded(prev => ({ ...prev, payments: true }));
            }
        },
        {
            id: 'statements',
            label: 'Statements',
            icon: <Receipt size={18} />,
            preload: () => {
                preloadStatement();
                setTabsLoaded(prev => ({ ...prev, statements: true }));
            }
        },
        {
            id: 'Trail',
            label: 'Activity',
            icon: <Activity size={18} />,
            preload: () => setTabsLoaded(prev => ({ ...prev, Trail: true }))
        },
        {
            id: 'Orders',
            label: 'Orders',
            icon: <ShoppingBag size={18} />,
            preload: () => {
                preloadOrders();
                setTabsLoaded(prev => ({ ...prev, Orders: true }));
            }
        },
    ], []);

    const sendEmail = useCallback(async () => {
        setSending(true);
        try {
            await sendPasswordSetEmail(customer_id);
            toast.success('Password set email sent');
        } catch (err) {
            toast.error(err.message || "Failed to send");
        } finally {
            setSending(false);
        }
    }, [customer_id]);

    const handleAddPayment = useCallback(() => {
        setAdd(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setAdd(false);
    }, []);

    const handleTabChange = useCallback((tabId) => {
        setTabsLoaded(prev => ({ ...prev, [tabId]: true }));
        setActiveTab(tabId);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const customerData = await fetchCustomerById(id);
                setInfo(customerData.customer);

                const [contractData, paymentData, propertyData] = await Promise.all([
                    fetchCustomerContracts({
                        customer_id: Number(id),
                        page: 1,
                        pageSize: 100,
                    }),
                    getCustomerPayments(id),
                    getCustomerProperties(id)
                ]);

                setContracts(contractData.contracts);
                setPayments(paymentData.payments);

                const extractedProperties = propertyData.properties.flatMap(order =>
                    order.order_items.map(item => item.property)
                );
                setPlots(extractedProperties);
            } catch (err) {
                console.error('Error fetching data:', err);
                toast.error('Error fetching customer data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        // Start preloading some components that might be used later
        preloadStatement();
        preloadPayment();
        preloadOrders();
    }, [id]);

    // Full page loading state
    if (loading && !info.id) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-screen -mt-16">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mb-4 mx-auto"></div>
                        <p className="text-gray-600">Loading customer data...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="w-full max-w-7xl mx-auto">
                {/* Profile Header */}
                <motion.div
                    {...fadeIn}
                    className="relative mb-8 overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-100 via-yellow-50 to-yellow-100 opacity-50"></div>
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iLjAyIj48cGF0aCBkPSJNMzYgMzRoLTJWMGgydjM0em0tNCAwaC0yVjBoMnYzNHptLTQgMGgtMlYwaDJ2MzR6bS00IDBoLTJWMGgydjM0em0tNCAwSDEwVjBoMnYzNHptLTQgMEg2VjBoMnYzNHptLTQgMEgyVjBoMnYzNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-10"></div>

                    <div className="relative p-8 rounded-3xl backdrop-blur-sm">
                        <div className="flex flex-col md:flex-row gap-6">
                            <div className="flex-shrink-0">
                                <div className="w-24 h-24 flex items-center justify-center rounded-2xl bg-gradient-to-br from-yellow-400 to-yellow-500 shadow-lg shadow-yellow-200/30">
                                    <User size={36} className="text-white" />
                                </div>
                            </div>

                            <div className="flex-1">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                                    <h1 className="text-2xl text-gray-900 font-bold flex items-center gap-2">
                                        {info.first_name} {info.last_name}
                                        {info.status && (
                                            <Badge
                                                type={info.status === 'active' ? 'active' : 'inactive'}
                                                text={info.status}
                                            />
                                        )}
                                    </h1>

                                    <div className="flex flex-wrap gap-2">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={sendEmail}
                                            disabled={sending}
                                            className="px-4 py-2 bg-yellow-500 text-white rounded-xl text-sm font-medium shadow-sm flex items-center gap-2"
                                        >
                                            {sending ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                    <span>Sending...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Mail size={16} />
                                                    <span>Send Password Email</span>
                                                </>
                                            )}
                                        </motion.button>

                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => navigate(`/dashboard/customers/${id}/update`)}
                                            className="px-4 py-2 bg-blue-500 text-white rounded-xl text-sm font-medium shadow-sm flex items-center gap-2"
                                        >
                                            <Edit size={16} />
                                            <span>Edit Profile</span>
                                        </motion.button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
                                    <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm p-2 rounded-xl">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                                            <Phone size={16} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">Phone</div>
                                            <div className="font-medium text-sm text-gray-800">{info.phone_number || 'N/A'}</div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm p-2 rounded-xl">
                                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-500">
                                            <Mail size={16} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">Email</div>
                                            <div className="font-medium text-sm text-gray-800">{info.email || 'N/A'}</div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm p-2 rounded-xl">
                                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-500">
                                            <Calendar size={16} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">Date of Birth</div>
                                            <div className="font-medium text-sm text-gray-800">
                                                {info.dob ? moment(info.dob).format('DD MMM, YYYY') : 'N/A'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm p-2 rounded-xl">
                                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-500">
                                            <MapPin size={16} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">Location</div>
                                            <div className="font-medium text-sm text-gray-800">
                                                {info.city && info.country ? `${info.city}, ${info.country}` : 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Tab Navigation */}
                <motion.div
                    {...fadeIn}
                    className="mb-8 overflow-x-auto pb-2 hide-scrollbar"
                >
                    <div className="flex gap-2">
                        {tabs.map(tab => (
                            <TabButton
                                key={tab.id}
                                id={`${tab.id}-tab-button`}
                                label={tab.label}
                                icon={tab.icon}
                                isActive={activeTab === tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                onMouseEnter={tab.preload}
                            />
                        ))}
                    </div>
                </motion.div>

                <div className="space-y-6">
                    <div style={{ display: activeTab === 'overview' ? 'block' : 'none' }}>
                        {tabsLoaded.overview && <OverviewTab info={info} />}
                    </div>

                    <div style={{ display: activeTab === 'plots' ? 'block' : 'none' }}>
                        {tabsLoaded.plots && <PlotsTab plots={plots} navigate={navigate} />}
                    </div>

                    <div style={{ display: activeTab === 'contracts' ? 'block' : 'none' }}>
                        {tabsLoaded.contracts && <ContractsTab contracts={contracts} navigate={navigate} />}
                    </div>

                    <div style={{ display: activeTab === 'payments' ? 'block' : 'none' }}>
                        {tabsLoaded.payments && <PaymentsTab payments={payments} loading={loading} handleAddPayment={handleAddPayment} />}
                    </div>

                    <div style={{ display: activeTab === 'statements' ? 'block' : 'none' }}>
                        {tabsLoaded.statements && (
                            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
                                <Suspense fallback={<TabLoadingFallback />}>
                                    <CustomerStatement />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    <div style={{ display: activeTab === 'Trail' ? 'block' : 'none' }}>
                        {tabsLoaded.Trail && <ActivityTab audits={info?.audits} />}
                    </div>

                    <div style={{ display: activeTab === 'Orders' ? 'block' : 'none' }}>
                        {tabsLoaded.Orders && (
                            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
                                <Suspense fallback={<TabLoadingFallback />}>
                                    <CustomerOrders />
                                </Suspense>
                            </div>
                        )}
                    </div>
                </div>

                {add && (
                    <Modal title="Add Payment" onClose={handleCloseModal}>
                        <Suspense fallback={<TabLoadingFallback />}>
                            <AddCustomerPayment onClose={handleCloseModal} />
                        </Suspense>
                    </Modal>
                )}
            </div>

            <style jsx global>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
        </Layout>
    );
};

export default CustomerProfile;